import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

const Warranty: React.FC = () => {
  // Preload hero image for better performance and scroll to top
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    
    const img = new Image();
    img.src = '/images/parallax-cinema-hero-image.png';
    
    // Dynamically inject GHL embed script once (if not already present)
    if (!document.querySelector("script[src='https://link.msgsndr.com/js/form_embed.js']")) {
      const script = document.createElement('script');
      script.src = 'https://link.msgsndr.com/js/form_embed.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <div className="min-h-screen">
      {/* PREMIUM PROTECTION HERO SECTION */}
      <section className="relative h-screen overflow-hidden">
        {/* Background: Elegant service/support environment */}
        <div className="absolute inset-0">
          <div 
            className="w-full h-full hero-image"
            style={{
              backgroundImage: 'url(/images/parallax-cinema-hero-image.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 h-full flex items-center">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div 
                className="glass-effect rounded-2xl p-8 md:p-12"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
              >
                <motion.h1 
                  className="text-5xl md:text-7xl font-light mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.3 }}
                >
                  <span className="text-gradient">Lifetime Protection</span>
                </motion.h1>
                
                <motion.p 
                  className="text-xl md:text-2xl text-secondary/90 mb-8 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.5 }}
                >
                  Register your Parallax Cinema products to activate comprehensive warranty 
                  coverage and unlock exclusive access to our lifetime concierge support services.
                </motion.p>
                
                <motion.div 
                  className="text-highlight/80 text-sm uppercase tracking-wider"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.7 }}
                >
                  Comprehensive Coverage • Priority Support • Exclusive Benefits
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* WARRANTY COVERAGE OVERVIEW */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                Comprehensive Coverage
              </h2>
              <p className="text-xl text-secondary/80 max-w-3xl mx-auto">
                Your investment is protected by industry-leading warranty coverage 
                and ongoing support that matches the quality of our products.
              </p>
            </motion.div>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Pro 8K Projector */}
              <motion.div 
                className="glass-effect rounded-xl p-8"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mr-4 flex items-center justify-center">
                    <span className="text-highlight text-xl">📽️</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-gradient">Pro 8K Projector</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Laser Light Source</span>
                    <span className="text-highlight font-medium">5 Years</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Electronics & Optics</span>
                    <span className="text-highlight font-medium">3 Years</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Labor & Service</span>
                    <span className="text-highlight font-medium">3 Years</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Firmware Updates</span>
                    <span className="text-highlight font-medium">Lifetime</span>
                  </div>
                </div>
              </motion.div>
              
              {/* Audio Systems */}
              <motion.div 
                className="glass-effect rounded-xl p-8"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mr-4 flex items-center justify-center">
                    <span className="text-highlight text-xl">🔊</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-gradient">Audio Systems</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Drivers & Components</span>
                    <span className="text-highlight font-medium">7 Years</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Electronics & DSP</span>
                    <span className="text-highlight font-medium">5 Years</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Cabinet & Finish</span>
                    <span className="text-highlight font-medium">10 Years</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Calibration Support</span>
                    <span className="text-highlight font-medium">Lifetime</span>
                  </div>
                </div>
              </motion.div>
              
              {/* Vision Screen */}
              <motion.div 
                className="glass-effect rounded-xl p-8"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mr-4 flex items-center justify-center">
                    <span className="text-highlight text-xl">🖼️</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-gradient">Vision Screen</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Screen Material</span>
                    <span className="text-highlight font-medium">10 Years</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Tensioning System</span>
                    <span className="text-highlight font-medium">7 Years</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Frame & Hardware</span>
                    <span className="text-highlight font-medium">15 Years</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Installation Service</span>
                    <span className="text-highlight font-medium">2 Years</span>
                  </div>
                </div>
              </motion.div>
              
              {/* Lifetime Benefits */}
              <motion.div 
                className="glass-effect rounded-xl p-8"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mr-4 flex items-center justify-center">
                    <span className="text-highlight text-xl">♾️</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-gradient">Lifetime Benefits</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Concierge Support</span>
                    <span className="text-highlight font-medium">Lifetime</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">System Optimization</span>
                    <span className="text-highlight font-medium">Lifetime</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Upgrade Consultation</span>
                    <span className="text-highlight font-medium">Lifetime</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-secondary/10 pb-2">
                    <span className="text-secondary/80">Priority Service</span>
                    <span className="text-highlight font-medium">Lifetime</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* WARRANTY REGISTRATION FORM (GOHIGHLEVEL EMBEDDED) */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                Register Your Products
              </h2>
              <p className="text-xl text-secondary/80 max-w-3xl mx-auto">
                Complete the form below to activate your warranty coverage and gain access 
                to exclusive support services. You'll receive instant confirmation via email and text.
              </p>
            </motion.div>
            
            <motion.div 
              className="glass-effect rounded-2xl p-8 md:p-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="mb-8">
                <h3 className="text-2xl font-semibold mb-4 text-gradient text-center">
                  Product Registration
                </h3>
                <p className="text-secondary/80 text-center text-sm">
                  Registration activates warranty coverage and unlocks exclusive benefits. 
                  All fields are required for complete protection.
                </p>
              </div>
              
              {/* GoHighLevel Warranty Registration Form */}
              <div className="rounded-lg overflow-hidden shadow-xl">
                <iframe
                  src="https://api.leadconnectorhq.com/widget/form/hwktferfRp8c1fVdHSJu"
                  style={{ width: '100%', height: '661px', border: 'none', borderRadius: '25px' }}
                  id="inline-hwktferfRp8c1fVdHSJu"
                  data-layout="{'id':'INLINE'}"
                  data-trigger-type="alwaysShow"
                  data-trigger-value=""
                  data-activation-type="alwaysActivated"
                  data-activation-value=""
                  data-deactivation-type="neverDeactivate"
                  data-deactivation-value=""
                  data-form-name="Warranty Registration Form - Parallax"
                  data-height="661"
                  data-layout-iframe-id="inline-hwktferfRp8c1fVdHSJu"
                  data-form-id="hwktferfRp8c1fVdHSJu"
                  title="Warranty Registration Form - Parallax"
                  loading="lazy"
                  className="transition-opacity duration-300 opacity-100"
                />
              </div>
              
              <div className="mt-8 text-center">
                <div className="glass-effect rounded-lg p-4 inline-block">
                  <h4 className="text-lg font-semibold mb-2 text-gradient">
                    What Happens Next?
                  </h4>
                  <div className="text-secondary/80 text-sm space-y-1">
                    <div>✓ Instant email confirmation with warranty details</div>
                    <div>✓ SMS confirmation to your mobile device</div>
                    <div>✓ Access to exclusive owner resources</div>
                    <div>✓ Priority support activation</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SUPPORT RESOURCES SECTION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-light mb-8 text-gradient">
                Ongoing Support Resources
              </h2>
              <p className="text-xl text-secondary/80 max-w-3xl mx-auto">
                Your registration unlocks access to comprehensive support resources 
                and exclusive owner benefits.
              </p>
            </motion.div>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Owner Portal */}
              <motion.div 
                className="glass-effect rounded-xl p-8 text-center hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-highlight text-2xl">🔐</span>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Owner Portal</h3>
                <p className="text-secondary/80">
                  Exclusive access to firmware updates, calibration guides, and 
                  advanced configuration resources.
                </p>
              </motion.div>
              
              {/* Priority Support */}
              <motion.div 
                className="glass-effect rounded-xl p-8 text-center hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-highlight text-2xl">📞</span>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Priority Support</h3>
                <p className="text-secondary/80">
                  Direct access to our technical specialists with guaranteed 
                  response times and priority scheduling.
                </p>
              </motion.div>
              
              {/* Upgrade Program */}
              <motion.div 
                className="glass-effect rounded-xl p-8 text-center hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-highlight text-2xl">⬆️</span>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Upgrade Program</h3>
                <p className="text-secondary/80">
                  Exclusive access to new products, trade-in programs, and 
                  loyalty pricing on system expansions.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT & FAQ SECTION */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16">
              
              {/* Contact Information */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-light mb-8 text-gradient">
                  Warranty Support
                </h2>
                
                <div className="space-y-6">
                  <div className="glass-effect rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-3 text-gradient">
                      Technical Support
                    </h3>
                    <div className="space-y-2 text-secondary/80">
                      <div>📞 (555) 123-SUPPORT</div>
                      <div>✉️ support@parallaxcinema.com</div>
                      <div>🕒 24/7 for registered products</div>
                    </div>
                  </div>
                  
                  <div className="glass-effect rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-3 text-gradient">
                      Service Coordination
                    </h3>
                    <div className="space-y-2 text-secondary/80">
                      <div>📞 (555) 123-SERVICE</div>
                      <div>✉️ service@parallaxcinema.com</div>
                      <div>🕒 Monday-Friday: 8AM-6PM PST</div>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* Warranty FAQ */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-light mb-8 text-gradient">
                  Frequently Asked Questions
                </h2>
                
                <div className="space-y-4">
                  <div className="glass-effect rounded-lg p-6">
                    <h3 className="font-semibold text-white mb-2">
                      When does my warranty coverage begin?
                    </h3>
                    <p className="text-secondary/80 text-sm">
                      Coverage begins immediately upon registration, backdated to your 
                      original purchase date (within 90 days of purchase).
                    </p>
                  </div>
                  
                  <div className="glass-effect rounded-lg p-6">
                    <h3 className="font-semibold text-white mb-2">
                      What if I can't find my warranty code?
                    </h3>
                    <p className="text-secondary/80 text-sm">
                      Contact our support team with your purchase details. We can locate 
                      your product information and provide registration assistance.
                    </p>
                  </div>
                  
                  <div className="glass-effect rounded-lg p-6">
                    <h3 className="font-semibold text-white mb-2">
                      Are labor costs covered under warranty?
                    </h3>
                    <p className="text-secondary/80 text-sm">
                      Yes, all labor costs including on-site service are covered for the 
                      duration of your warranty period at no additional charge.
                    </p>
                  </div>
                  
                  <div className="glass-effect rounded-lg p-6">
                    <h3 className="font-semibold text-white mb-2">
                      Can I extend my warranty coverage?
                    </h3>
                    <p className="text-secondary/80 text-sm">
                      Extended service plans are available up to 10 years total coverage. 
                      Contact us within your original warranty period for options.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Warranty; 
